<script lang="ts">
  import { moneyCards } from "../lib/moneyCards.svelte.ts";
  import { link } from "../lib/router.svelte.ts";
  import { groupPath } from "../lib/routes.ts";
  import { untrack } from "svelte";
  import { customSplitText, formatBp, splitItem, type Item, type ItemFields } from "@split/core";
  import Drawer from "./Drawer.svelte";
  import CardPicker from "./CardPicker.svelte";
  import Swatch from "./Swatch.svelte";
  import SplitControls from "./SplitControls.svelte";
  import { app } from "../lib/store.svelte.ts";
  import { centsInput, formatMicro, readMoney, readPercent, shortDateTime, today, tidyName } from "../lib/format.ts";

  let { item = null, preset = {}, onclose }: { item?: Item | null; preset?: Partial<ItemFields>; onclose: () => void } = $props();

  const G = $derived(app.current);
  const isNew = $derived(item === null);

  // ---- form fields (text, so typing "12." doesn't jump around)
  // The form starts from the item (or preset) as it was when the panel opened.
  const start = untrack(() => item);
  const pre = untrack(() => preset);
  let name = $state(start?.name ?? pre.name ?? "");
  let store = $state(start?.store ?? pre.store ?? lastStore());
  let date = $state(start?.date ?? pre.date ?? today());
  let itemNo = $state(start?.itemNo ?? "");
  let price = $state(start ? centsInput(start.priceCents) : "");
  let coupon = $state(start ? centsInput(-start.couponCents) : "");
  let crv = $state(start ? centsInput(start.crvCents) : "");
  let tax = $state(start && start.taxBp !== null ? String(start.taxBp / 100) : "");
  let tip = $state(start && start.tipBp !== null && start.tipBp !== undefined ? String(start.tipBp / 100) : "");
  let category = $state(start?.category ?? "");
  let paidWith = $state(start?.paidWith ?? pre.paidWith ?? "");
  let receipt = $state(start?.receipt ?? pre.receipt ?? "");
  let paidBy = $state(start?.paidBy ?? "");
  // With today's names (the split itself is kept by person, so renames never change it).
  let customSplit = $state(start ? untrack(() => customSplitText(G, start)) : "");
  let note = $state(start?.note ?? "");
  let ticked = $state<string[]>(start ? [...start.ticked] : []);
  let showMore = $state(Boolean(start && (start.couponCents || start.crvCents || start.paidBy || start.note || start.category)));
  let saving = $state(false);

  function lastStore(): string {
    const order = app.current.itemOrder;
    const last = order.length ? app.current.items[order[order.length - 1]!] : undefined;
    return last?.store ?? "Costco";
  }

  const stores = $derived([...new Set([...Object.keys(G.stores), ...Object.values(G.items).map((i) => i.store)])].sort());
  const people = $derived(G.personOrder.map((id) => G.people[id]!).filter((p) => p && !p.archived));
  /** Receipt labels already used on this date at this store, for the picker. */
  const receiptsHere = $derived(
    [...new Set(Object.values(G.items).filter((i) => i.date === date && i.store === store.trim() && i.receipt).map((i) => i.receipt!))].sort(
      (a, b) => a.localeCompare(b, undefined, { numeric: true }),
    ),
  );
  const receiptLabel = (r: string) => (/^\d+$/.test(r) ? `Receipt ${r}` : r);
  const nextReceipt = $derived(String(Math.max(1, ...receiptsHere.map((r) => Number(r) || 1)) + 1));
  // Big groups: search, and ticked people first, so hundreds of chips stay usable.
  let whoQuery = $state("");
  const whoList = $derived.by(() => {
    if (people.length <= 12) return people;
    const q = whoQuery.trim().toLowerCase();
    const on = people.filter((p) => ticked.includes(p.id));
    const rest = people.filter((p) => !ticked.includes(p.id) && (!q || p.name.toLowerCase().includes(q)));
    return [...on, ...(q ? rest : rest.slice(0, 24))];
  });
  const storeTax = $derived(G.stores[store.trim()]?.taxBp);
  const storeTip = $derived(G.stores[store.trim()]?.tipBp);
  const storeCategory = $derived(G.stores[store.trim()]?.category);

  const errors = $derived.by(() => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = "Add a description";
    if (!store.trim()) e.store = "Add a store";
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) e.date = "Pick a date";
    if (readMoney(price || "0") === null) e.price = "Enter an amount like 12.99";
    if (readMoney(coupon || "0") === null) e.coupon = "Enter an amount like 2.80";
    if (readMoney(crv || "0") === null) e.crv = "Enter an amount like 0.90";
    if (tax.trim() !== "" && readPercent(tax) === null) e.tax = "Enter a percent like 7.75";
    if (tip.trim() !== "" && readPercent(tip) === null) e.tip = "Enter a percent like 18";
    return e;
  });
  const valid = $derived(Object.keys(errors).length === 0);

  function fields(): ItemFields {
    return {
      date,
      // "costco" -> "Costco" so presets and grouping match; mixed capitals ("iHerb") are kept.
      store: store.trim() === store.trim().toLowerCase() ? tidyName(store) : store.trim(),
      itemNo: itemNo.trim(),
      name: name.trim(),
      category: category || null,
      priceCents: readMoney(price || "0") ?? 0,
      couponCents: -Math.abs(readMoney(coupon || "0") ?? 0),
      crvCents: Math.abs(readMoney(crv || "0") ?? 0),
      taxBp: tax.trim() === "" ? null : readPercent(tax),
      tipBp: tip.trim() === "" ? null : readPercent(tip),
      paidWith: paidWith || null,
      paidBy: paidBy || null,
      customSplit: customSplit.trim(),
      note,
      receipt: receipt.trim(),
      // Added from a receipt's "Add item" and still on it: that receipt, by id.
      ...(!item && pre.receiptId && date === pre.date && store.trim() === pre.store && receipt.trim() === (pre.receipt ?? "")
        ? { receiptId: pre.receiptId }
        : {}),
    };
  }

  /** Live preview: the total and each person's share as the form stands. */
  const preview = $derived.by(() => {
    if (!valid) return null;
    const draft: Item = { id: "draft", ...fields(), ticked };
    return splitItem(G, draft);
  });

  const catalogHint = $derived.by(() => {
    const no = itemNo.trim();
    if (!no || !isNew) return null;
    const prev = app.ledger.catalog.find((c) => c.itemNo === no);
    return prev ?? null;
  });

  function useCatalog() {
    if (!catalogHint) return;
    name = catalogHint.name;
    price = centsInput(catalogHint.lastPriceCents);
  }

  function toggle(pid: string) {
    ticked = ticked.includes(pid) ? ticked.filter((p) => p !== pid) : [...ticked, pid];
  }

  async function save() {
    if (!valid || saving) return;
    saving = true;
    const f = fields();
    if (!item) {
      const err = await app.do_({ type: "item.add", itemId: crypto.randomUUID(), fields: f, ticked }, `Added ${f.name}`);
      saving = false;
      if (!err) onclose();
      return;
    }
    const patch: Partial<ItemFields> = {};
    for (const k of Object.keys(f) as (keyof ItemFields)[]) {
      const was =
        k === "receipt" ? (item.receipt ?? "") : k === "tipBp" ? (item.tipBp ?? null) : k === "customSplit" ? customSplitText(G, item) : item[k];
      if (JSON.stringify(f[k]) !== JSON.stringify(was)) (patch as Record<string, unknown>)[k] = f[k];
    }
    let err: string | null = null;
    if (Object.keys(patch).length) err = await app.do_({ type: "item.update", itemId: item.id, patch });
    for (const pid of people.map((p: { id: string }) => p.id)) {
      if (err) break;
      const was = item.ticked.includes(pid);
      const now = ticked.includes(pid);
      if (was !== now) err = await app.do_({ type: "share.set", itemId: item.id, personId: pid, ticked: now });
    }
    saving = false;
    if (!err) {
      app.toast("Saved");
      onclose();
    }
  }

  async function remove() {
    if (!item) return;
    const copy = $state.snapshot(item) as Item;
    const err = await app.do_({ type: "item.remove", itemId: item.id });
    if (err) return;
    onclose();
    app.toast(`Removed ${copy.name}`, "info", {
      label: "Undo",
      run: () => {
        const { id, ticked: t, ...rest } = copy;
        void app.do_({ type: "item.add", itemId: id, fields: rest, ticked: t.filter((p) => app.current.people[p]) });
      },
    });
  }

  const itemHistory = $derived(item ? app.history.filter((h) => h.itemId === item!.id).slice().reverse() : []);
  const ownerId = $derived(G.settings.ownerPersonId);
</script>

<Drawer title={app.memberSession ? "Item" : isNew ? "Add item" : "Edit item"} {onclose}>
  <form class="form" onsubmit={(e) => { e.preventDefault(); void save(); }} id="item-form">
  <fieldset class="plain" disabled={!!app.memberSession}>
    <label class="field">
      <span>Description</span>
      <input class="input" bind:value={name} placeholder="Rotisserie chicken" aria-invalid={!!errors.name && name !== ""} required />
    </label>

    <div class="row2">
      <label class="field">
        <span>Store</span>
        <input class="input" bind:value={store} list="stores" aria-invalid={!!errors.store} />
        <datalist id="stores">{#each stores as s}<option value={s}></option>{/each}</datalist>
      </label>
      <label class="field">
        <span>Date</span>
        <input class="input" type="date" bind:value={date} aria-invalid={!!errors.date} />
      </label>
    </div>

    <div class="row2">
      <label class="field">
        <span>Receipt</span>
        <select class="input" bind:value={receipt}>
          <option value="">{receiptsHere.length ? "Receipt 1 (main)" : "One receipt"}</option>
          {#each receiptsHere as r}<option value={r}>{receiptLabel(r)}</option>{/each}
          {#if receipt && !receiptsHere.includes(receipt)}<option value={receipt}>{receiptLabel(receipt)}</option>{/if}
          <option value={nextReceipt}>New receipt ({nextReceipt})</option>
        </select>
      </label>
      <div class="field">
        <span class="flabel"><span>Card</span><a class="edit-list" href={`${groupPath(app.groupId ?? "", "settings")}?tab=lists`} use:link>Edit cards</a></span>
        <CardPicker bind:value={paidWith} cards={moneyCards.with(G.settings.cards)} label="Card" />
      </div>
    </div>

    <div class="row4">
      <label class="field">
        <span>Price</span>
        <input class="input money" inputmode="decimal" bind:value={price} placeholder="0.00" aria-invalid={!!errors.price} />
      </label>
      <label class="field">
        <span>Tax %</span>
        <input class="input money" inputmode="decimal" bind:value={tax} placeholder={storeTax !== undefined ? formatBp(storeTax) : "0%"} aria-invalid={!!errors.tax} />
      </label>
      <label class="field">
        <span>Tip %</span>
        <input class="input money" inputmode="decimal" bind:value={tip} placeholder={storeTip ? formatBp(storeTip) : "0%"} aria-invalid={!!errors.tip} />
      </label>
      <label class="field">
        <span>Item #</span>
        <input class="input" inputmode="numeric" bind:value={itemNo} placeholder="Optional" />
      </label>
    </div>
    {#if catalogHint}
      <p class="hint-line">
        Last bought {catalogHint.lastDate} as <strong>{catalogHint.name}</strong> for {formatMicro(catalogHint.lastPriceCents * 10_000)}.
        <button type="button" class="link" onclick={useCatalog}>Use that</button>
      </p>
    {/if}
    <p class="hint-line quick">
      {#if storeTax === undefined && tax === "" && store.trim()}{store.trim()} has no preset, so tax is 0% unless you enter one.{:else}Empty uses {store.trim() || "the store"}'s preset.{/if}
      <button type="button" class="link" onclick={() => (tax = "0")}>No tax</button>
      <button type="button" class="link" onclick={() => (tip = "0")}>No tip</button>
      {#if tax !== "" || tip !== ""}<button type="button" class="link" onclick={() => (tax = tip = "")}>Use preset</button>{/if}
    </p>

    <fieldset class="who">
      <legend>Who's it for</legend>
      {#if people.length > 12}
        <div class="who-tools">
          <input class="input" type="search" bind:value={whoQuery} placeholder="Find someone" aria-label="Find someone" />
          <span class="muted small">{ticked.length} ticked</span>
          {#if ticked.length}<button type="button" class="link" onclick={() => (ticked = [])}>Clear</button>{/if}
        </div>
      {/if}
      <div class="chips">
        {#each whoList as p (p.id)}
          {@const on = ticked.includes(p.id)}
          <button type="button" class="chip" class:on aria-pressed={on} onclick={() => toggle(p.id)}>
            <Swatch color={p.color} status={on ? "paid" : "unpaid"} size={18} />
            {p.name}{p.id === ownerId ? " (you)" : ""}
            {#if on && preview?.shares[p.id] !== undefined}
              <span class="share num">{formatMicro(preview.shares[p.id]!)}</span>
            {/if}
          </button>
        {/each}
      </div>
      <div class="field split">
        <span>How to split</span>
        <SplitControls
          bind:value={customSplit}
          ticked={people.filter((p) => ticked.includes(p.id))}
          everyone={G.personOrder.map((id) => G.people[id]!).filter(Boolean)}
          shares={preview?.shares}
          errors={preview?.errors ?? []}
        />
      </div>
    </fieldset>

    {#if !showMore}
      <button type="button" class="btn quiet small more" onclick={() => (showMore = true)}>Coupon, CRV, who paid, notes</button>
    {:else}
      <div class="row2">
        <label class="field">
          <span>Coupon</span>
          <input class="input money" inputmode="decimal" bind:value={coupon} placeholder="0.00" aria-invalid={!!errors.coupon} />
        </label>
        <label class="field">
          <span>CRV</span>
          <input class="input money" inputmode="decimal" bind:value={crv} placeholder="0.00" aria-invalid={!!errors.crv} />
        </label>
      </div>
      <div class="row2">
        <label class="field">
          <span>Paid the store</span>
          <select class="input" bind:value={paidBy}>
            <option value="">You</option>
            {#each people.filter((p) => p.id !== ownerId) as p}<option value={p.id}>{p.name}</option>{/each}
          </select>
        </label>
      </div>
      <div class="field">
        <span class="flabel"><span>Category</span><a class="edit-list" href={`${groupPath(app.groupId ?? "", "settings")}?tab=lists`} use:link>Edit categories</a></span>
        <select class="input" aria-label="Category" bind:value={category}>
          <option value="">{storeCategory ? `Store default (${storeCategory})` : "Store default"}</option>
          {#each G.settings.categories as c}<option value={c}>{c}</option>{/each}
        </select>
      </div>
      <label class="field">
        <span>Note</span>
        <textarea class="input" rows="2" bind:value={note}></textarea>
      </label>
    {/if}

    {#if itemHistory.length}
      <section class="hist">
        <h3>Changes to this item</h3>
        <ol>
          {#each itemHistory as h (h.event.id)}
            <li>
              <span class="muted">{shortDateTime(h.event.at)}</span>
              {h.text}
              {#each h.changes as c}<div class="chg">{c.field}: <s>{c.before}</s> {c.after}</div>{/each}
            </li>
          {/each}
        </ol>
      </section>
    {/if}
  </fieldset>
  </form>

  {#snippet footer()}
    <div class="total">
      <span class="muted">Total</span>
      <strong class="num">{preview ? formatMicro(preview.totalMicro) : "-"}</strong>
    </div>
    {#if app.memberSession}
      <span class="muted small">Only the treasurer changes items.</span>
    {:else}
      {#if !isNew}<button class="btn quiet danger" onclick={remove} disabled={!app.canEdit}>Remove</button>{/if}
      <button class="btn primary" type="submit" form="item-form" disabled={!valid || saving || !app.canEdit}>
        {isNew ? "Add item" : "Save changes"}
      </button>
    {/if}
  {/snippet}
</Drawer>

<style>
  .plain {
    border: 0;
    padding: 0;
    margin: 0;
    min-width: 0;
    display: contents;
  }

  .flabel {
    display: flex;
    gap: 6px;
    align-items: baseline;
  }

  .edit-list {
    margin-left: 6px;
    font-size: var(--t-xs);
    font-weight: 500;
  }

  .form {
    display: grid;
    gap: 16px;
  }

  .row2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .row4 {
    display: grid;
    grid-template-columns: 1.2fr 0.8fr 0.8fr 1fr;
    gap: 12px;
  }

  .quick .link {
    margin-left: 8px;
  }

  .hint-line {
    margin-top: -8px;
    font-size: var(--t-xs);
    color: var(--ink-2);
  }

  .link {
    border: 0;
    background: none;
    padding: 0;
    color: var(--focus);
    font-weight: 600;
    cursor: pointer;
    text-decoration: underline;
  }

  .who {
    border: 1px solid var(--rule);
    border-radius: var(--r-lg);
    padding: 12px 14px 14px;
    margin: 0;
    background: var(--surface);
    display: grid;
    gap: 14px;
  }

  legend {
    padding: 0 6px;
    font-size: var(--t-xs);
    font-weight: 600;
    color: var(--ink-2);
  }

  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .who-tools {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
  }

  .who-tools .input {
    flex: 1;
  }

  .who-tools .small {
    font-size: var(--t-xs);
  }

  .chip {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    min-height: 36px;
    padding: 0 12px 0 8px;
    border-radius: 999px;
    border: 1px solid var(--rule-strong);
    background: var(--paper);
    font-size: var(--t-sm);
    font-weight: 550;
    cursor: pointer;
  }

  .chip.on {
    background: var(--sel);
    border-color: transparent;
  }

  .share {
    color: var(--ink-2);
    font-weight: 500;
  }

  .more {
    justify-self: start;
    text-decoration: underline;
  }

  .hist h3 {
    font-size: var(--t-sm);
    margin-bottom: 6px;
  }

  .hist ol {
    margin: 0;
    padding: 0;
    list-style: none;
    display: grid;
    gap: 6px;
    font-size: var(--t-xs);
  }

  .chg {
    color: var(--ink-2);
    padding-left: 8px;
  }

  s {
    color: var(--muted);
  }

  .total {
    margin-right: auto;
    display: grid;
    line-height: 1.1;
  }

  .total strong {
    font-size: var(--t-xl);
  }

  @media (max-width: 480px) {
    .row4 {
      grid-template-columns: 1fr 1fr;
    }
  }
</style>
